import React, { useState, useEffect } from 'react';
import { Button } from '../ui';
import { CartaoForm } from './CartaoForm';
import { PixDisplay } from './PixDisplay';
import { VoucherForm } from './VoucherForm';
import { StatusPagamento } from './StatusPagamento';
import InscricaoService from '../../services/Inscricoes/InscricaoService';
import { CheckCircle2, FileText, CreditCard, Zap, ArrowLeft, Ticket, Users } from 'lucide-react';

export function CheckoutPage({ inscricaoId, valorTotal, nomeCorrida, inscricoesLote, onSuccess, onFinished, onError }) {
  const [metodoPagamento, setMetodoPagamento] = useState(null); // 'cartao' | 'pix'
  const [resultado, setResultado] = useState(null);
  const [inscricaoData, setInscricaoData] = useState(null);

  const isLote = Array.isArray(inscricoesLote) && inscricoesLote.length > 0;

  useEffect(() => {
    // Se for checkout em lote com inscrições já fornecidas, não busca inscrição individual
    if (isLote) {
      return;
    }

    if (inscricaoId) {
      let isMounted = true;
      const fetchInscricao = async () => {
        try {
          const service = new InscricaoService();
          const res = await service.getInscricaoById(inscricaoId);
          if (res && res.ok && isMounted) {
            const data = await res.json();
            setInscricaoData(data);
          }
        } catch (err) {
          console.warn('Erro ao verificar status da inscrição no checkout:', err);
        }
      };
      fetchInscricao();
      return () => {
        isMounted = false;
      };
    }
  }, [inscricaoId, isLote]);

  const handleSuccess = (res) => {
    setResultado(res);
    onSuccess?.(res);
  };

  if (resultado) {
    return (
      <StatusPagamento
        resultado={resultado}
        corridaNome={nomeCorrida || inscricaoData?.nome_corrida}
        onFinished={onFinished}
      />
    );
  }

  // Se for lote, prioriza o valorTotal do lote (já consolidado pelo backend ou grupo).
  // Caso contrário, usa o valor_pagamento da inscrição individual (ex: desconto 60+) ou o valorTotal passado.
  const somaLote = isLote
    ? inscricoesLote.reduce((acc, item) => acc + Number(item.valor_pagamento ?? item.valor ?? 0), 0)
    : null;

  const currentValor = isLote
    ? (valorTotal !== undefined && valorTotal !== null ? valorTotal : somaLote)
    : (inscricaoData?.valor_pagamento !== undefined && inscricaoData?.valor_pagamento !== null
        ? inscricaoData.valor_pagamento
        : valorTotal);

  const currentStatus = inscricaoData?.status;
  const isGratuita = isLote
    ? (currentValor !== undefined && parseFloat(currentValor || 0) === 0) || inscricoesLote.every(i => i.status === 'Inscrito' || i.status === 'Gratuita')
    : (inscricaoData?.is_gratuita || inscricaoData?.corrida?.is_gratuita);

  const isFreeOrConfirmed = 
    isGratuita ||
    (currentValor !== undefined && parseFloat(currentValor || 0) === 0) || 
    currentStatus === 'Inscrito';

  if (isFreeOrConfirmed) {
    return (
      <div className="max-w-xl mx-auto p-6 bg-urb-card border border-urb-border rounded-2xl shadow-xl text-center">
        <div className="flex flex-col items-center mb-6">
          <CheckCircle2 className="w-16 h-16 text-urb-primary mb-3" />
          <h2 className="text-2xl font-bold text-white">Inscrição Confirmada!</h2>
          {(nomeCorrida || inscricaoData?.nome_corrida || inscricaoData?.corrida?.nome_corrida) && (
            <p className="text-slate-300 font-medium text-lg mt-1">
              {nomeCorrida || inscricaoData?.nome_corrida || inscricaoData?.corrida?.nome_corrida}
            </p>
          )}
          <p className="font-semibold text-sm mt-2 text-urb-primary">
            Esta inscrição é gratuita e já está confirmada.
          </p>
        </div>

        <div className="flex justify-center mt-6">
          <Button
            onClick={() => {
              window.location.href = `/pagamento/sucesso?id=${inscricaoId}`;
            }}
            className="flex items-center gap-2 px-6 py-3 font-bold shadow-neon"
          >
            <FileText className="w-4 h-4" />
            Ver Comprovante
          </Button>
        </div>
      </div>
    );
  }

  const formattedValor = typeof currentValor === 'number'
    ? currentValor.toFixed(2)
    : parseFloat(currentValor || 0).toFixed(2);

  return (
    <div className="max-w-xl mx-auto p-6 bg-urb-card border border-urb-border rounded-2xl shadow-xl">
      <div className="text-center pb-5 border-b border-urb-border mb-6">
        <h2 className="text-2xl font-extrabold text-white">Finalizar Inscrição</h2>
        {nomeCorrida && (
          <p className="text-slate-300 font-medium text-sm mt-1">{nomeCorrida}</p>
        )}
        <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-urb-primary/10 border border-urb-primary/30">
          <span className="text-xs uppercase tracking-wider text-slate-300 font-semibold">Total a Pagar:</span>
          <span className="text-xl font-black text-urb-primary">R$ {formattedValor}</span>
        </div>

        {Array.isArray(inscricoesLote) && inscricoesLote.length > 1 && (
          <div className="mt-4 p-3 rounded-xl bg-slate-900/80 border border-slate-800 text-left">
            <span className="text-xs font-bold text-slate-400 flex items-center gap-1.5 mb-2">
              <Users className="w-4 h-4 text-urb-primary" />
              Inscrições neste pedido ({inscricoesLote.length} atletas):
            </span>
            <div className="flex flex-wrap gap-1.5">
              {inscricoesLote.map((ins, i) => {
                const nome = ins.nome_corredor || [ins.first_name, ins.last_name].filter(Boolean).join(" ") || `Atleta #${i + 1}`;
                return (
                  <span key={ins.id || i} className="text-xs px-2.5 py-1 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 font-medium">
                    {nome} {ins.is_terceiro ? "(Terceiro)" : "(Titular)"}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {!metodoPagamento && (
        <div className="flex flex-col gap-4">
          <p className="text-center text-slate-400 text-sm font-medium">
            Escolha como deseja pagar:
          </p>
          <div className={`grid grid-cols-1 ${inscricoesLote && inscricoesLote.length > 1 ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-4`}>
            <button
              type="button"
              onClick={() => setMetodoPagamento('cartao')}
              className="flex flex-col items-center justify-center p-5 rounded-xl border border-urb-border bg-slate-900/60 hover:border-urb-primary hover:bg-urb-primary/5 transition-all text-white group"
            >
              <CreditCard className="w-8 h-8 text-slate-400 group-hover:text-urb-primary mb-2 transition-colors" />
              <span className="font-bold text-base">Cartão de Crédito</span>
              <span className="text-xs text-slate-400 mt-1">Aprovação imediata</span>
            </button>

            <button
              type="button"
              onClick={() => setMetodoPagamento('pix')}
              className="flex flex-col items-center justify-center p-5 rounded-xl border border-urb-border bg-slate-900/60 hover:border-urb-primary hover:bg-urb-primary/5 transition-all text-white group"
            >
              <Zap className="w-8 h-8 text-slate-400 group-hover:text-urb-primary mb-2 transition-colors" />
              <span className="font-bold text-base">Pix Instantâneo</span>
              <span className="text-xs text-slate-400 mt-1">QR Code dinâmico</span>
            </button>

            {(!inscricoesLote || inscricoesLote.length <= 1) && (
              <button
                type="button"
                onClick={() => setMetodoPagamento('voucher')}
                className="flex flex-col items-center justify-center p-5 rounded-xl border border-urb-border bg-slate-900/60 hover:border-urb-primary hover:bg-urb-primary/5 transition-all text-white group"
              >
                <Ticket className="w-8 h-8 text-slate-400 group-hover:text-urb-primary mb-2 transition-colors" />
                <span className="font-bold text-base">Possuo um voucher</span>
                <span className="text-xs text-slate-400 mt-1">Cupom ou cortesia</span>
              </button>
            )}
          </div>
        </div>
      )}

      {metodoPagamento === 'voucher' && (
        <div>
          <button
            type="button"
            onClick={() => setMetodoPagamento(null)}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Alterar forma de pagamento
          </button>
          <VoucherForm
            inscricaoId={inscricaoData?.id || inscricoesLote?.[0]?.id || inscricaoId}
            corridaId={inscricaoData?.id_corrida?.id || inscricaoData?.id_corrida || inscricaoData?.corrida?.id || inscricaoData?.corrida || inscricoesLote?.[0]?.id_corrida}
            onSuccess={handleSuccess}
            onError={(err) => {
              console.error('Erro voucher:', err);
              onError?.(err);
            }}
          />
        </div>
      )}

      {metodoPagamento === 'cartao' && (
        <div>
          <button
            type="button"
            onClick={() => setMetodoPagamento(null)}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Alterar forma de pagamento
          </button>
          <CartaoForm
            inscricaoId={inscricaoId}
            valorTotal={currentValor}
            onSuccess={handleSuccess}
            onError={(err) => {
              console.error('Erro cartão:', err);
              onError?.(err);
            }}
          />
        </div>
      )}

      {metodoPagamento === 'pix' && (
        <div>
          <button
            type="button"
            onClick={() => setMetodoPagamento(null)}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Alterar forma de pagamento
          </button>
          <PixDisplay
            inscricaoId={inscricaoId}
            valorTotal={currentValor}
            onSuccess={handleSuccess}
            onError={(err) => {
              console.error('Erro pix:', err);
              onError?.(err);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default CheckoutPage;
